import { Component, OnInit, OnDestroy, ViewEncapsulation, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AuthDavesaV2Service } from 'app/core/auth-davesa/auth-davesa-v2.service';
import { Subject, takeUntil } from 'rxjs';

/**
 * Password Expiry Warning Banner Component
 * 
 * Displays a dismissible warning banner when user's password is expiring within 7 days
 * Shows across all authenticated layouts
 * 
 * Features:
 * - Only shows when password expires in 7 days or less
 * - Dismissible by user
 * - Provides link to password change page
 * - ARIA-compliant for accessibility
 * 
 * @example
 * <!-- In layout component -->
 * <password-expiry-banner></password-expiry-banner>
 */
@Component({
  selector: 'password-expiry-banner',
  templateUrl: './password-expiry-banner.component.html',
  // styleUrls: ['./password-expiry-banner.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, RouterLink, MatIconModule, MatButtonModule]
})
export class PasswordExpiryBannerComponent {
  private _router = inject(Router);
  private _authDavesaV2Service = inject(AuthDavesaV2Service);

  /**
   * Signal to track if banner is dismissed
   */
  isDismissed = signal(false);

  /**
   * Signal to track if banner should be shown
   */
  shouldShow = computed(() => {
    if (this.isDismissed()) {
      return false;
    }

    const loginUserStatus = this._authDavesaV2Service.loginUserStatus();
    
    if (!loginUserStatus) {
      return false;
    }

    // Show banner if password expires in 7 days or less (but not expired)
    const passwordExpiresInDays = loginUserStatus?.passwordExpiresInDays;
    return passwordExpiresInDays !== undefined && 
           passwordExpiresInDays > 0 && 
           passwordExpiresInDays <= 7;
  });

  /**
   * Days until password expires
   */
  daysUntilExpiry = computed(() => {
    const loginUserStatus = this._authDavesaV2Service.loginUserStatus();
    return loginUserStatus?.passwordExpiresInDays || 0;
  });

  /**
   * Banner message
   */
  message = computed(() => {
    const days = this.daysUntilExpiry();
    if (days === 1) {
      return 'Your password expires tomorrow. Please change it soon to avoid interruption.';
    }
    return `Your password expires in ${days} days. Please change it soon to avoid interruption.`;
  });

  /**
   * Dismiss banner
   */
  dismiss(): void {
    this.isDismissed.set(true);
  }

  /**
   * Navigate to password change page
   */
  changePassword(): void {
    this._router.navigate(['/password-change']);
  }
}

